import React, { useContext, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
} from '@mui/material';
import { UploadFile, Visibility, CloudUpload } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';

const PRIORITY_LABEL = { 0: 'Baixa', 1: 'Média', 2: 'Alta', 3: 'Urgente' };

const MovideskImportPage = () => {
  const { theme } = useContext(ThemeContext);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [limit, setLimit] = useState('2');
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    setPreview(null);
    setSummary(null);
  };

  const handlePreview = async () => {
    if (!files.length) {
      toast.warning('Selecione ao menos um arquivo XML do Movidesk.');
      return;
    }
    setLoadingPreview(true);
    setSummary(null);
    try {
      const form = new FormData();
      form.append('file', files[0]);
      if (limit) form.append('limit', limit);
      const res = await http.post('/movidesk-import/preview', form, { timeout: 120000 });
      setPreview({ ...res.data, fileName: files[0].name });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao pré-visualizar o arquivo.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!files.length) {
      toast.warning('Selecione ao menos um arquivo XML do Movidesk.');
      return;
    }
    const qtd = limit ? `os primeiros ${limit} de cada arquivo` : 'TODOS os';
    if (!window.confirm(`Confirma importar ${qtd} tickets de ${files.length} arquivo(s)? Tickets já importados são ignorados.`)) {
      return;
    }
    setLoadingImport(true);
    setSummary(null);
    const agg = { created: 0, skipped: 0, errors: 0, files_ok: 0, files_failed: 0 };
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress({ current: i + 1, total: files.length, name: files[i].name });
        const form = new FormData();
        form.append('file', files[i]);
        if (limit) form.append('limit', limit);
        try {
          const res = await http.post('/movidesk-import/run', form, { timeout: 600000 });
          const s = res.data.summary;
          agg.created += s.created;
          agg.skipped += s.skipped;
          agg.errors += s.errors;
          agg.files_ok += 1;
        } catch (err) {
          agg.files_failed += 1;
        }
      }
      setSummary(agg);
      toast.success(`Importação concluída: ${agg.created} criados, ${agg.skipped} ignorados.`);
    } finally {
      setLoadingImport(false);
      setProgress(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        Importar tickets do Movidesk
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Envie um ou mais arquivos XML exportados do Movidesk. Os tickets são adaptados ao formato do Alodesk
        (status, prioridade, categoria, contato e as ações viram comentários). Tickets já importados são ignorados.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".xml,text/xml,application/xml"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button variant="outlined" startIcon={<UploadFile />} onClick={() => fileInputRef.current?.click()}>
            Selecionar XML(s)
          </Button>
          <Typography variant="body2" color={files.length ? 'text.primary' : 'text.secondary'}>
            {files.length === 0
              ? 'Nenhum arquivo selecionado'
              : files.length === 1
                ? files[0].name
                : `${files.length} arquivos selecionados`}
          </Typography>

          <Tooltip title="Máximo de tickets por arquivo (deixe vazio para importar todos)" arrow>
            <TextField
              size="small"
              label="Limite/arquivo"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              sx={{ width: 130, ml: 'auto' }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={loadingPreview ? <CircularProgress size={16} /> : <Visibility />}
            onClick={handlePreview}
            disabled={loadingPreview || loadingImport || !files.length}
          >
            Pré-visualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={loadingImport ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            onClick={handleImport}
            disabled={loadingPreview || loadingImport || !files.length}
          >
            Importar
          </Button>
        </Box>

        {progress && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Importando arquivo {progress.current} de {progress.total} — {progress.name}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.round((progress.current / progress.total) * 100)}
              sx={{ mt: 0.5, borderRadius: 1 }}
            />
          </Box>
        )}
      </Paper>

      {summary && (
        <Alert severity={summary.errors > 0 || summary.files_failed > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
          Importação concluída — <strong>{summary.created}</strong> criados,{' '}
          <strong>{summary.skipped}</strong> ignorados (já existiam),{' '}
          <strong>{summary.errors}</strong> com erro
          {summary.files_failed > 0 && <> · <strong>{summary.files_failed}</strong> arquivo(s) com falha</>}.
        </Alert>
      )}

      {preview && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Demonstração — {preview.sample_size} de {preview.total_in_file} tickets
            <Typography component="span" variant="body2" color="text.secondary">
              {' '}(arquivo {preview.fileName}
              {preview.already_imported_in_sample > 0 && <>, {preview.already_imported_in_sample} já importados</>})
            </Typography>
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, backgroundColor: theme.palette.background.default } }}>
                  <TableCell>Movidesk #</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Contato</TableCell>
                  <TableCell align="center">Ações</TableCell>
                  <TableCell>Situação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.sample.map((t) => (
                  <TableRow key={t.movidesk_number} hover>
                    <TableCell>{t.movidesk_number}</TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={t.title} arrow>
                        <span>{t.title}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Movidesk: ${t.movidesk_status}`} arrow>
                        <span>{t.movidesk_status}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Urgência Movidesk: ${t.movidesk_urgency}`} arrow>
                        <span>{PRIORITY_LABEL[t.priority] ?? t.priority}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{t.category || <em style={{ color: theme.palette.text.disabled }}>—</em>}</TableCell>
                    <TableCell>{t.contact || '—'}</TableCell>
                    <TableCell align="center">{t.actions_count}</TableCell>
                    <TableCell>
                      {t.already_imported ? (
                        <Chip size="small" label="Já importado" color="default" />
                      ) : (
                        <Chip size="small" label="Novo" color="success" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default MovideskImportPage;
